import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Mailbox } from '@mailpilot/shared';
import { ChevronDown, FlaskConical, Send } from 'lucide-react';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { rawFetch } from '../../lib/api';
import { qk } from '../../lib/keys';
import { isValidEmail } from '../../lib/format';
import { Button, Field, InlineError, Input, Textarea, spring, useToast } from '../ui';

interface DemoIncoming {
  fromName: string;
  fromEmail: string;
  subject: string;
  body: string;
}

const EMPTY: DemoIncoming = { fromName: '', fromEmail: '', subject: '', body: '' };

/**
 * Simulates a new incoming message on a demo mailbox.
 * NOTE: POST /api/mailboxes/:id/demo-incoming is not part of the ApiRoutes contract yet.
 */
export function AccountsDemoIncoming({ mb }: { mb: Mailbox }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [f, setF] = useState<DemoIncoming>(EMPTY);
  const [open, setOpen] = useState(false);

  const send = useMutation({
    mutationFn: async (body: DemoIncoming) => {
      await rawFetch(`/api/mailboxes/${encodeURIComponent(mb.id)}/demo-incoming`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      toast.success('Simulated email delivered to the demo inbox');
      setF(EMPTY);
      void qc.invalidateQueries({ queryKey: qk.threadsRoot });
      void qc.invalidateQueries({ queryKey: qk.mailboxes });
    },
  });

  const valid = isValidEmail(f.fromEmail) && f.subject.trim() && f.body.trim();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (valid) send.mutate(f);
  };

  return (
    <div className="rounded-2xl bg-info-soft/50 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-info-soft text-info-ink">
            <FlaskConical className="h-4 w-4" aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="font-medium text-ink">Simulate incoming email</p>
            <p className="mt-0.5 text-[0.8125rem] leading-relaxed text-muted">This demo mailbox holds sample mail, not a live connection. Deliver a message to test rules and acknowledgements safely.</p>
          </div>
        </div>
        <Button size="sm" variant={open ? 'secondary' : 'soft'} aria-expanded={open} iconRight={<ChevronDown className={clsx('h-4 w-4 transition-transform', open && 'rotate-180')} aria-hidden />} onClick={() => setOpen((o) => !o)}>
          {open ? 'Hide form' : 'Write a test email'}
        </Button>
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={spring} className="overflow-hidden">
            <form onSubmit={submit} className="space-y-5 pt-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="From name">{(id) => <Input id={id} value={f.fromName} onChange={(e) => setF({ ...f, fromName: e.target.value })} placeholder="Ada Lovelace" />}</Field>
                <Field label="From email" required>
                  {(id) => <Input id={id} type="email" value={f.fromEmail} onChange={(e) => setF({ ...f, fromEmail: e.target.value })} placeholder="ada@example.com" />}
                </Field>
              </div>
              <Field label="Subject" required>{(id) => <Input id={id} value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} />}</Field>
              <Field label="Body" required>{(id) => <Textarea id={id} rows={4} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />}</Field>
              <InlineError error={send.error} />
              <div className="flex justify-end">
                <Button type="submit" variant="primary" size="sm" disabled={!valid} loading={send.isPending} icon={<Send className="h-4 w-4" aria-hidden />}>
                  Deliver to demo inbox
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
